import React from 'react';

import styled from 'styled-components';

import { Box, Button, FormField } from 'components';
import { useNotifications } from 'hooks';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useUser } from 'modules/user';

import { userSelectors } from 'selectors';

enum FormFields {
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
}

type FormData = {
  [FormFields.firstName]: string;
  [FormFields.lastName]: string;
  [FormFields.email]: string;
};

type Props = {
  onModalClose: () => void;
};

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1.25rem;
`;

const ModalTitleArea = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.custom.palette.textDark};
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
`;

const ModalSubtitle = styled.p`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${props => props.theme.custom.palette.textGray};
  line-height: 1.4;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const OutlineButton = styled(Button)`
  font-weight: 600 !important;
  font-size: 0.8125rem !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-radius: 8px !important;
  padding: 0.625rem 1.25rem !important;
  border-color: ${props => props.theme.custom.palette.primaryOrange} !important;
  color: ${props => props.theme.custom.palette.primaryOrange} !important;
  background-color: transparent !important;

  &:hover {
    background-color: rgba(230, 106, 16, 0.1) !important;
  }
`;

export const InviteUserForm: React.FC<Props> = ({ onModalClose }) => {
  const { error, success } = useNotifications();
  const { inviteUser } = useUser();
  const { t } = useTranslation();
  const inviteUserPending = useSelector(userSelectors.inviteUserPending);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>();

  const isFormPending = inviteUserPending;

  const onSubmit = async (formData: FormData) => {
    const result = await inviteUser(formData);
    if (result.error) {
      error(result.payload.body?.title || t('userForm.edit.error'));
    } else {
      success(t('userForm.edit.sucscefull'));
      onModalClose();
    }
    return;
  };

  return (
    <Box>
      <form>
        <ModalHeader>
          <ModalTitleArea>
            <ModalTitle>{t('userFor.invite.header')}</ModalTitle>
            <ModalSubtitle>{t('userFor.invite.subHeader')}</ModalSubtitle>
          </ModalTitleArea>
          <ModalActions>
            <OutlineButton
              type="submit"
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              disabled={isFormPending}
              size="medium"
            >
              {t('common.form.invite')}
            </OutlineButton>
            <OutlineButton
              type="button"
              variant="outlined"
              onClick={onModalClose}
              disabled={isFormPending}
              size="medium"
            >
              {t('common.form.cancel')}
            </OutlineButton>
          </ModalActions>
        </ModalHeader>

        <FormField
          fieldName={FormFields.firstName}
          fieldError={errors[FormFields.firstName] as FieldError}
          label={t('membersList.column.firstName')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.firstName')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.lastName}
          fieldError={errors[FormFields.lastName] as FieldError}
          label={t('membersList.column.lastName')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.lastName')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.email}
          fieldError={errors[FormFields.email] as FieldError}
          label={t('membersList.column.email')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.email')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
      </form>
    </Box>
  );
};
